import type { AgentV2, PrincipalCredentialsV2 } from "@manifold/protocol";

/**
 * The Sessions section's one policy decision, as a pure function (#145).
 *
 * A principal with no live credential cannot act again until somebody mints for it anew, so
 * its row is HISTORY, not status — and on a workspace that has hosted gate runs and agent
 * re-enrolments, history outnumbers the living by an order of magnitude (~250 dead agent
 * rows beside 8 real identities on the operator's own instance, 2026-09-01). The section
 * shows the living by default and folds the rest into one collapsed disclosure: never
 * deleted, never hidden from a reader who asks, never again the first thing a reader wades
 * through.
 *
 * Order within each half is the server's own (`listCredentials` answers newest-last today),
 * because re-sorting here would make this module a second opinion about a list the server
 * already owns.
 */
export interface PartitionedCredentials {
  /** Principals holding at least one live credential — the rows that can still act. */
  readonly live: readonly PrincipalCredentialsV2[];
  /** Credential-less principals: the audit trail, folded shut by default. */
  readonly inactive: readonly PrincipalCredentialsV2[];
}

export function partitionCredentials(
  rows: readonly PrincipalCredentialsV2[],
): PartitionedCredentials {
  const live: PrincipalCredentialsV2[] = [];
  const inactive: PrincipalCredentialsV2[] = [];
  for (const row of rows) {
    (row.sessions.length > 0 ? live : inactive).push(row);
  }
  return { live, inactive };
}

/** Keep the server's order within each half of the Agents rail. */
export function partitionAgents(rows: readonly AgentV2[]): {
  readonly live: readonly AgentV2[];
  readonly retired: readonly AgentV2[];
} {
  const live: AgentV2[] = [];
  const retired: AgentV2[] = [];
  for (const row of rows) {
    (row.state === "retired" ? retired : live).push(row);
  }
  return { live, retired };
}
